import { useMemo, useState } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Hash,
  ChevronLeft,
  RefreshCw,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Search,
  BadgeCheck,
  Lock,
} from "lucide-react";

import PageGate from "../../../components/PageGate";
import { PERMS } from "../../../utils/perms";
import {
  useProbationerEntrySeriesQuery,
  useProbationerMemberNumbersQuery,
  useIssueProbationerMemberNumberMutation,
} from "../api/probationerApplicationApi";

/**
 * Entry numbers — student (S), probationer (P), technician (T).
 *
 * Two questions on one screen, because they are asked together:
 *
 *   · WHERE DOES EACH SERIES STAND — the last number handed out in each
 *     category and what the next would be. Three cards at the top, all three
 *     always shown: the series run independently and a gap in one is only
 *     visible beside the others.
 *   · WHO STILL NEEDS ONE — the diet's approved applicants, each with the
 *     number they hold or the number they would get. Numbered and unnumbered
 *     in one table, since "what number did this person get" is the same
 *     question a day later.
 *
 * Issued ONE AT A TIME and on purpose. The next free number is prefilled so
 * taking it is a single press, and the field is editable so a number already
 * written on paperwork can be typed in instead. There is no "number everyone"
 * button: these are permanent, they cannot be taken back, and a batch would
 * stamp the register faster than anyone could check it.
 */

function fmtDate(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Where one category's series stands. */
function SeriesCard({ row }: { row: any }) {
  const s = row.series ?? {};

  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">
            {row.category?.name}
          </div>
          <div className="mt-1 text-2xl font-black tabular-nums text-gray-900">
            {s.highest_text ?? "—"}
          </div>
          <div className="text-[11px] font-bold text-gray-400">
            Last issued
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">
            Next
          </div>
          <div className="mt-1 text-lg font-black tabular-nums text-blue-600">
            {s.next_text ?? "—"}
          </div>
        </div>
      </div>

      {/* Where the figure came from. A "next" that surprises somebody is
          traced here rather than by reading the code: the register itself
          outranks the stored counter, and an imported spreadsheet is what
          usually moves it. */}
      <div className="mt-4 pt-3 border-t border-gray-50 text-[11px] font-semibold text-gray-400">
        {s.seeded_from === "register"
          ? "Read off the register"
          : s.seeded_from === "settings"
          ? "From the recorded counter"
          : "Nothing issued yet — starts at the configured floor"}
        {s.prefix ? ` · prefix ${s.prefix}` : " · no prefix"}
      </div>

      {!row.available ? (
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-100 bg-amber-50 p-2.5 text-[11px] font-bold text-amber-800">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          This series cannot be issued yet — its register column is missing on
          the server.
        </div>
      ) : null}
    </div>
  );
}

export default function AdminProbationerMemberNumbersPage() {
  const { dietId = "", categoryId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    data: seriesRows = [],
    isLoading: seriesLoading,
    refetch: refetchSeries,
  } = useProbationerEntrySeriesQuery();

  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = useProbationerMemberNumbersQuery(
    { dietId, categoryId },
    { skip: !dietId },
  );

  const [issue, { isLoading: issuing }] = useIssueProbationerMemberNumberMutation();

  const diet = data?.diet;
  const rows: any[] = useMemo(() => data?.applications ?? [], [data]);
  const counts = data?.counts ?? { approved: 0, numbered: 0, awaiting: 0 };

  const [q, setQ] = useState("");
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);
  /* The number being typed for one row, keyed by application id. Absent means
     "use what the server proposed" — the input shows the proposal as its
     placeholder, so an untouched field takes the next number. */
  const [edits, setEdits] = useState<Record<string, string>>({});
  /* Which row is mid-flight. `issuing` alone would grey out every button. */
  const [busyRow, setBusyRow] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      `${r.name ?? ""} ${r.email ?? ""} ${r.membership_no ?? ""} ${r.category ?? ""} ${
        r.chapter ?? ""
      }`
        .toLowerCase()
        .includes(s),
    );
  }, [rows, q]);

  const canIssue = diet?.can_issue !== false;

  const assign = async (row: any) => {
    setBanner(null);
    setBusyRow(row.id);

    const typed = (edits[row.id] ?? "").trim();

    try {
      const res: any = await issue({
        applicationId: row.id,
        membership_no: typed || undefined,
      }).unwrap();

      setBanner({ ok: true, text: res?.message ?? "Number issued." });
      setEdits((e) => {
        const next = { ...e };
        delete next[row.id];
        return next;
      });
    } catch (e: any) {
      setBanner({
        ok: false,
        text: e?.data?.message || "That number could not be issued.",
      });
    } finally {
      setBusyRow(null);
    }
  };

  return (
    <PageGate perm={PERMS.application_diet.memberNumberIssue}>
      <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
        {/* Header */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-gray-900"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                <Hash className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                  Membership numbers
                </h1>
                <p className="text-sm font-semibold text-gray-500">
                  {diet?.title ||
                    (location.state as any)?.dietTitle ||
                    "Entry applications"}
                  {" · "}
                  {counts.numbered} of {counts.approved} approved numbered
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                refetch();
                refetchSeries();
              }}
              disabled={isFetching}
              className="p-2.5 bg-white border border-gray-100 rounded-xl hover:bg-gray-50 shadow-sm"
            >
              <RefreshCw
                className={`w-4 h-4 text-gray-500 ${isFetching ? "animate-spin" : ""}`}
              />
            </button>
          </div>

          {/* The diet rule, stated before anything is pressed rather than as a
              refusal afterwards. */}
          {!canIssue ? (
            <div className="mt-4 flex items-start gap-3 rounded-2xl border border-amber-100 bg-amber-50 p-4">
              <Lock className="w-5 h-5 text-amber-500 shrink-0" />
              <div className="text-sm font-semibold text-amber-800">
                This is not the active diet, so numbers cannot be issued here.
                The numbers each applicant already holds are still shown.
              </div>
            </div>
          ) : null}
        </div>

        {banner ? (
          <div
            className={`px-4 py-3 rounded-2xl text-sm font-semibold border flex items-start gap-2 ${
              banner.ok
                ? "bg-emerald-50 border-emerald-100 text-emerald-800"
                : "bg-rose-50 border-rose-100 text-rose-800"
            }`}
          >
            {banner.ok ? (
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            )}
            {banner.text}
          </div>
        ) : null}

        {/* ── Where each series stands ─────────────────────────────────── */}
        <div>
          <h2 className="text-lg font-black text-gray-900">Series</h2>
          <p className="text-sm text-gray-500 mb-4">
            The last number issued in each category, and the next one out. Each
            category is its own running series — an S, a P and a T number with
            the same digits are three different people.
          </p>

          {seriesLoading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-40 bg-gray-50 border border-gray-100 rounded-3xl animate-pulse"
                />
              ))}
            </div>
          ) : seriesRows.length === 0 ? (
            <p className="text-sm text-gray-400">
              No category has a number series configured.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {seriesRows.map((row: any) => (
                <SeriesCard key={row.category?.id} row={row} />
              ))}
            </div>
          )}
        </div>

        {/* ── Approved applicants ──────────────────────────────────────── */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-black text-gray-900">
                Approved applicants
              </h2>
              <p className="text-sm text-gray-500">
                Issuing a number puts the applicant on the members register.
                The next free number is filled in for you — type over it to use
                a different one.
              </p>
              <p className="mt-1 text-[11px] font-bold uppercase tracking-widest text-gray-400">
                {counts.awaiting} awaiting a number · {counts.numbered} numbered
              </p>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, number or chapter…"
                className="pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="mt-6 h-48 bg-gray-50 rounded-2xl animate-pulse" />
          ) : rows.length === 0 ? (
            <p className="mt-6 text-sm text-gray-400">
              No application in this diet has been approved yet, so there is
              nothing to number.
            </p>
          ) : filtered.length === 0 ? (
            <p className="mt-6 text-sm text-gray-400">
              Nothing matches “{q}”.
            </p>
          ) : (
            <div className="mt-4 border rounded-2xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                    <th className="px-4 py-2">Applicant</th>
                    <th className="px-4 py-2">Category</th>
                    <th className="px-4 py-2">Approved</th>
                    <th className="px-4 py-2">Number</th>
                    <th className="px-4 py-2 text-right" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((r) => {
                    const rowBusy = issuing && busyRow === r.id;
                    const blocked = !!r.series_missing_reason;

                    return (
                      <tr key={r.id} className="align-middle">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">
                            {r.name || "—"}
                          </div>
                          <div className="text-[11px] text-gray-400">
                            {r.email || r.chapter || "—"}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {r.category || "—"}
                          {r.series_label ? (
                            <div className="text-[11px] text-gray-400">
                              {r.series_label} series
                            </div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                          {fmtDate(r.approved_at)}
                        </td>

                        <td className="px-4 py-3">
                          {r.numbered ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-[11px] font-black tabular-nums">
                              <BadgeCheck className="w-3.5 h-3.5" />
                              {r.membership_no}
                            </span>
                          ) : blocked ? (
                            <span className="text-[11px] font-semibold text-amber-700">
                              {r.series_missing_reason}
                            </span>
                          ) : (
                            <input
                              value={edits[r.id] ?? ""}
                              onChange={(e) =>
                                setEdits((s) => ({ ...s, [r.id]: e.target.value }))
                              }
                              placeholder={r.proposed_membership_no ?? "Number"}
                              disabled={!canIssue || rowBusy}
                              className="w-32 px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-bold tabular-nums outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-gray-50"
                            />
                          )}
                        </td>

                        <td className="px-4 py-3 text-right">
                          {r.numbered ? (
                            <span className="text-[11px] font-bold uppercase tracking-widest text-gray-300">
                              Issued
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => assign(r)}
                              disabled={!canIssue || blocked || rowBusy}
                              title={
                                canIssue
                                  ? "Issue this number and put the applicant on the register"
                                  : "Only the active diet can issue numbers"
                              }
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-900 text-white text-[11px] font-black uppercase tracking-widest hover:bg-blue-600 disabled:opacity-40"
                            >
                              {rowBusy ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Hash className="w-3.5 h-3.5" />
                              )}
                              Issue
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="p-6 bg-blue-50/50 rounded-3xl border border-blue-100 text-xs font-bold text-blue-700/70 leading-relaxed">
          A number is never reissued and never handed out twice: the register
          itself is checked before every issue, not just the stored counter, so
          a number typed in by hand or arriving on an imported spreadsheet
          cannot be given to a second person. Correcting a number already
          issued is a secretariat job on the member's own record — see{" "}
          <Link to="/settings" className="underline">
            portal settings
          </Link>{" "}
          for the shape of each series.
        </div>
      </div>
    </PageGate>
  );
}
